import { DemoDashboardId, User } from 'src/models';
import { ExperimentProps } from '../growthbook/types';
import {
  DEMO_REPORT_ALL_USERS_FILTERS,
  isDemoReportGroupId,
  isDemoReportUserId
} from './demo-report-users.mock';

/** GrowthBook experiment that controls whether dashboard mock/sample data is eligible to show. */
export const SHOW_MOCK_DATA_EXPERIMENT_KEY = 'show-mock-data-exp' as const;

/** Inputs that come from auth/company/manager state (after the feature flag gate). */
export interface DashboardMockDataVisibilityInput {
  users: User[];
  /** Company-level last tracked project id (`AuthCompany.lastTrack?.projectId`). */
  companyLastTrackProjectId: unknown;
  /** `userSettings.custom.demoDataHiddenDashboards` for the current user. */
  demoDataHiddenDashboards: DemoDashboardId[] | undefined;
  /** Company-wide `companySettings.custom.showDemoData` (read for every role). */
  showDemoData: boolean | undefined;
  trackingMode: string | undefined;
  currentRoute: string;
}

/** Step 1 — GrowthBook: only variant `B` enables mock data for the session. */
export function isMockDataExperimentVariantB(
  experiment: ExperimentProps | null | undefined,
): boolean {
  return experiment?.variationid === 'B';
}

function dashboardPath(route: string): string {
  const withoutQueryHash = (route ?? '').split('?')[0]?.split('#')[0] ?? '';
  return withoutQueryHash.split(';')[0] ?? '';
}

function decodeRouteParam(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function parseRouteParamMap(route: string): Record<string, string> {
  const withoutHash = (route ?? '').split('#')[0] ?? '';
  const [pathAndMatrix, query = ''] = withoutHash.split('?');
  const params: Record<string, string> = {};

  for (const part of (pathAndMatrix ?? '').split(';').slice(1)) {
    const eq = part.indexOf('=');
    if (eq <= 0) {
      continue;
    }
    params[decodeRouteParam(part.slice(0, eq))] = decodeRouteParam(part.slice(eq + 1));
  }

  for (const part of query.split('&')) {
    if (!part) {
      continue;
    }
    const eq = part.indexOf('=');
    if (eq <= 0) {
      continue;
    }
    params[decodeRouteParam(part.slice(0, eq))] = decodeRouteParam(part.slice(eq + 1));
  }

  return params;
}

function splitRouteIds(raw?: string): string[] {
  if (!raw?.trim()) {
    return [];
  }
  return [...new Set(raw.split(',').map(id => id.trim()).filter(Boolean))];
}

/** True for catalog mock-store user or group ids (`user-1`, `tag-1`, …). */
export function isDemoStoreEntityId(id: string | null | undefined): boolean {
  return isDemoReportUserId(id) || isDemoReportGroupId(id);
}

/**
 * True when the URL selects a live employee or group (matrix or query `user` / `group` / `userIds` / `tagIds`).
 * Catalog ids and `all-on-reports` do not count — sample data should stay on for those.
 */
export function routeHasRealEntitySelection(route: string): boolean {
  const params = parseRouteParamMap(route);
  const users = [...splitRouteIds(params.user), ...splitRouteIds(params.userIds)];
  const groups = [...splitRouteIds(params.group), ...splitRouteIds(params.tag), ...splitRouteIds(params.tagIds)];

  const hasLiveUser = users.some(id => !DEMO_REPORT_ALL_USERS_FILTERS.has(id) && !isDemoReportUserId(id));
  const hasLiveGroup = groups.some(id => !isDemoReportGroupId(id));
  return hasLiveUser || hasLiveGroup;
}

/** True when `projectId` / `deviceId`-like values count as real tracked time. */
export function hasTrackedProjectId(projectId: unknown): boolean {
  if (projectId === undefined || projectId === null) {
    return false;
  }
  if (typeof projectId === 'string') {
    return projectId.length > 0;
  }
  return Boolean(projectId);
}

export function getDemoDashboardIdFromRoute(route: string): DemoDashboardId | undefined {
  const path = dashboardPath(route);
  if (path === '/dashboard-executive' || path.startsWith('/dashboard-executive/')) {
    return 'executive';
  }
  if (path === '/dashboard-individual' || path.startsWith('/dashboard-individual/')) {
    return 'individual';
  }
  if (path === '/dashboard-team' || path.startsWith('/dashboard-team/')) {
    return 'team';
  }
  if (path === '/dashboard' || path.startsWith('/dashboard/')) {
    return 'team';
  }
  if (path === '/meeting-insights' || path.startsWith('/meeting-insights/')) {
    return 'meeting-insights';
  }
  if (path === '/benchmarks' || path.startsWith('/benchmarks/')) {
    return 'benchmarksAi';
  }
  if (path === '/office-vs-remote' || path.startsWith('/office-vs-remote/')) {
    return 'office-vs-remote';
  }
  if (path === '/payroll' || path.startsWith('/payroll/')) {
    return 'payroll';
  }
  if (
    path === '/hours-tracked'
    || path.startsWith('/hours-tracked/')
    || path === '/hours-tracked-v2'
    || path.startsWith('/hours-tracked-v2/')
  ) {
    return 'hours-tracked';
  }
  if (path === '/approvals' || path.startsWith('/approvals/')) {
    return 'approvals';
  }
  if (path === '/breaks' || path.startsWith('/breaks/')) {
    return 'breaks';
  }
  if (
    path === '/projects-report'
    || path.startsWith('/projects-report/')
    || path === '/projects-report-v2'
    || path.startsWith('/projects-report-v2/')
  ) {
    return 'projects-report';
  }
  if (path === '/timeline' || path.startsWith('/timeline/')) {
    return 'timeline';
  }
  if (path === '/unusual-activity' || path.startsWith('/unusual-activity/')) {
    return 'unusual-activity';
  }
  if (path === '/screencasts' || path.startsWith('/screencasts/')) {
    return 'screencasts';
  }
  if (
    path === '/web-app-usage'
    || path.startsWith('/web-app-usage/')
    || path === '/productivity'
    || path.startsWith('/productivity/')
  ) {
    return 'web-app-usage';
  }
  if (path === '/activity-summary' || path.startsWith('/activity-summary/')) {
    return 'activity-summary';
  }
  if (path === '/internet-report' || path.startsWith('/internet-report/')) {
    return 'internet-connectivity';
  }
  if (path === '/attendance' || path.startsWith('/attendance/')) {
    return 'attendance';
  }
  return undefined;
}

export interface DashboardMockDataActiveEvaluation {
  /** Whether dashboard widgets should use sample/mock data for the current route. */
  isActive: boolean;
  /** True when mock data is off because the company or users have tracked time. */
  blockedByTrackedTime: boolean;
}

/**
 * After GrowthBook allows the experiment ({@link isMockDataExperimentVariantB}), decides if mock data
 * may show on the current route:
 * company tracked time → off; company-level `custom.showDemoData` falsy → off;
 * unknown / unwired route → off;
 * live user/group in the URL → off;
 * user tracked time (projectId / silent deviceId) → off;
 * user \`custom.demoDataHiddenDashboards\` contains the current dashboard id → off.
 *
 * Cheap company checks run before any O(n) scan of {@link DashboardMockDataVisibilityInput.users}.
 * Decision order stays aligned with {@link DemoService.showDemoData}.
 */
export function evaluateIsDashboardMockDataActive(
  input: DashboardMockDataVisibilityInput,
): DashboardMockDataActiveEvaluation {
  // O(1) company-level tracked time — prefer over scanning managed users.
  if (hasTrackedProjectId(input.companyLastTrackProjectId)) {
    return { isActive: false, blockedByTrackedTime: true };
  }

  // Legacy flag already off (or unset): mock cannot show; skip user scan.
  if (!input.showDemoData) {
    return { isActive: false, blockedByTrackedTime: false };
  }

  const dashboardId = getDemoDashboardIdFromRoute(input.currentRoute);
  if (!dashboardId) {
    return { isActive: false, blockedByTrackedTime: false };
  }

  // A live employee/group in the URL always wins over sample data (e.g. team widget → individual).
  if (routeHasRealEntitySelection(input.currentRoute)) {
    return { isActive: false, blockedByTrackedTime: false };
  }

  const userTracked =
    (input.users?.some(u => hasTrackedProjectId(u.lastTrack?.projectId)) ?? false) ||
    (input.trackingMode === 'silent' &&
      (input.users?.some(u => hasTrackedProjectId(u.lastTrack?.deviceId)) ?? false));

  if (userTracked) {
    return { isActive: false, blockedByTrackedTime: true };
  }

  if (input.demoDataHiddenDashboards?.includes(dashboardId)) {
    return { isActive: false, blockedByTrackedTime: false };
  }

  return { isActive: true, blockedByTrackedTime: false };
}

/** True when individual-dashboard widgets should hide links to other pages. */
export function shouldHideIndividualDashboardReportLinks(
  showDemoData: boolean,
  isIndividualDashboard: boolean | undefined,
): boolean {
  return showDemoData === true && isIndividualDashboard === true;
}

/** True when executive dashboard sample data should replace the upsell / invite flow. */
export function shouldBypassExecutiveDashboardUpsell(
  showDemoData: boolean | undefined,
): boolean {
  return showDemoData === true;
}

/** True when Benchmarks AI / Internet Connectivity report guards should allow access with sample data instead of menu/upsell checks. */
export function shouldBypassBenchmarksReportAccess(
  showDemoData: boolean | undefined,
): boolean {
  return showDemoData === true;
}

/** Alias for sample-data upsell bypass on Internet Connectivity and similar gated reports. */
export function shouldBypassReportUpsellWhenDemoData(
  showDemoData: boolean | undefined,
): boolean {
  return shouldBypassBenchmarksReportAccess(showDemoData);
}
